// Structured data behind /work (grouped in workIndex.js) and the case-study
// pages. Every item renders as the same tile: media, title and one meta line
// that says what the piece is and its field ("Case study · Payments").
//
// Media options on an item —
//
//   media:   { type: "image", src: importedPng }
//   media:   { type: "video", src: "/videos/mail" }   // extension-less: .webm + .mp4
//   gallery: [ ...MediaItem ]   // the dialog carousel; falls back to [media]
//
// Cards that link somewhere (case study, article, demo) send the whole surface
// — including the thumbnail — to that page. Cards with only a `gallery` open
// the dialog carousel from the card itself.
//
// Videos live in /public/videos as matching .webm + .mp4 pairs, plus a
// same-name `.webp` first-frame poster for browsers that never paint a frame.

import imgInstallmentsTransparent from "../assets/images/installmentsimg.webp";
// aisdlc-flow is the current illustration of the interaction contract; the
// earlier diagram stays in the folder as aisdlc.webp for fallback.
import imgAiSdlc from "../assets/images/aisdlc-flow.webp";
import imgHalftonePlugin from "../assets/images/halftone-plugin.webp";
import imgDesignToCodePlugins from "../assets/images/design-to-code-plugins.webp";
import imgAgainCard from "../assets/images/homepage/again/work-card.webp";
import imgTramatCard from "../assets/images/homepage/tramat/work-card.webp";

import imgWorkspaceAlert from "../assets/images/AI Workspace - Alert.webp";
import imgWorkspaceTeams from "../assets/images/homepage/ui/workspace-teams.webp";
import imgWorkspaceConversationAsk from "../assets/images/homepage/ui/workspace-conversation-ask.webp";
import imgWorkspaceConversationMap from "../assets/images/homepage/ui/workspace-conversation-map.webp";
import imgWorkspaceAnalysis from "../assets/images/AI Workspace - Analysis.webp";
import imgWorkspaceMerchantReview from "../assets/images/AI Workspace - Merchant review.webp";
import imgWorkspaceClosedTicket from "../assets/images/AI Workspace - Closed ticket.webp";

import imgVehicleOnboarding from "../assets/images/car-3.webp";
import imgVehicleControls from "../assets/images/car-1.webp";
import imgVehicleStatus from "../assets/images/car-2.webp";
import imgVehicleRemotePark from "../assets/images/car-4.webp";

import imgWalletsOnboarding from "../assets/images/wallets-2.webp";
import imgWalletsAddWallet from "../assets/images/wallets-4.webp";
import imgWalletsConversion from "../assets/images/wallets-3.webp";
import imgWalletsDetail from "../assets/images/wallets-5.webp";

import imgPortalDashboard from "../assets/images/homepage/ui/portal-dashboard.webp";
import imgPortalDashboardScroll from "../assets/images/homepage/ui/portal-dashboard-scroll.webp";
import imgPortalAiAnswer from "../assets/images/homepage/ui/portal-ai-answer.webp";
import imgPortalChatExpanded from "../assets/images/homepage/ui/portal-chat-expanded.webp";
import imgPortalShowWidget from "../assets/images/homepage/ui/portal-show-widget.webp";
import imgPortalAiCanvas from "../assets/images/homepage/ui/portal-ai-canvas.webp";
import imgPortalPayins from "../assets/images/homepage/ui/portal-payins.webp";
import imgPortalPayinDetail from "../assets/images/homepage/ui/portal-payin-detail.webp";
import imgPortalPaymentRequest from "../assets/images/homepage/ui/portal-payment-request.webp";

/**
 * @typedef {Object} MediaItem
 * @property {"image"|"video"} type
 * @property {string} src
 * @property {string} [alt]
 * @property {string} [background]       // opaque plate under artwork with alpha
 */

/**
 * @typedef {Object} WorkItem
 * @property {string} id
 * @property {"case-study"|"visual"|"code"} type
 * @property {MediaItem} [media]                  // card preview, gradient placeholder when absent
 * @property {MediaItem[]} [gallery]              // opens the dialog carousel
 * @property {string} title
 * @property {string} meta                        // what it is · its field, under the title
 * @property {{value:string, label:string}[]} [metrics]  // article proof, not on the tile
 * @property {{label:string, href:string, external?:boolean}} [cta]  // where the whole tile goes
 * @property {{label:string, short?:string, href:string, external?:boolean}[]} [links]  // the first is the whole tile's target; all are printed in the meta line, `short` printed and `label` their accessible name
 * @property {Chapter} [chapter]                  // the case study's homepage chapter
 */

/**
 * A case study as a homepage chapter: the idea it proves, said once.
 *
 * @typedef {Object} Chapter
 * @property {string} principle                   // the heading; the idea, not the project
 * @property {string} line                        // one sentence of what happened
 * @property {{value:string, label:string}} outcome
 * @property {"ai"|"design-system"|"installments"|"checkout"} diagram  // ConceptDiagram type
 */

/** @type {WorkItem[]} */
export const caseStudies = [
  {
    id: "cs-ai-workflow",
    type: "case-study",
    media: {
      type: "image",
      src: imgAiSdlc,
      alt: "Research, analytics, design, data and code inputs feeding an AI agent, a human review gate, then an approved spec that loops back as memory for the next cycle",
    },
    title: "Designing how people and AI work together",
    meta: "Case study · AI operations",
    metrics: [
      { value: "−72%", label: "Tickets needing manual review" },
      { value: "+12 pp", label: "Support CSAT" },
    ],
    cta: { label: "Read case study", href: "/case-study/ai-workflow" },
    chapter: {
      principle: "AI prepares. People decide.",
      line: "An operating model where AI gathers the evidence and specialists keep the decision.",
      outcome: { value: "−72%", label: "tickets needing full manual review" },
      diagram: "ai",
    },
  },
  {
    id: "cs-design-system",
    type: "case-study",
    // The three case-study loops were animated GIFs; they now ship as the same
    // webm + mp4 pairs the motion cards use, at a tenth of the weight.
    media: {
      type: "video",
      src: "/videos/ds-hero",
      alt: "The system’s home page opening: the headline Turn product intent into working interfaces fades in, then cursors for the roles that build from it arrive around it",
    },
    title: "The design system people and AI build every product from",
    meta: "Case study · Design systems",
    metrics: [
      { value: "200+", label: "Screens built" },
      { value: "Org-wide", label: "Frontend default" },
    ],
    cta: { label: "Read case study", href: "/case-study/design-system" },
    chapter: {
      principle: "One source of truth, for people and machines.",
      line: "Product UI made machine-readable, so design, engineering and AI build from the same parts on web, iOS and Android.",
      outcome: { value: "Org-wide", label: "default frontend workflow" },
      diagram: "design-system",
    },
  },
  {
    id: "cs-installments",
    type: "case-study",
    media: {
      type: "video",
      src: "/videos/installments-anim",
      alt: "Moving payment authorization from a single total to a split-plan choice",
    },
    title: "Doubling installment revenue by finding the right moment",
    meta: "Case study · Payments",
    metrics: [
      { value: "+114%", label: "Installment revenue" },
      { value: "~5% → ~49%", label: "Automatic-installment conversion" },
    ],
    cta: { label: "Read case study", href: "/case-study/installments" },
    chapter: {
      principle: "Find the decision, not the funnel.",
      line: "We mapped when people chose to split a payment, and offered plans at that moment.",
      outcome: { value: "+114%", label: "installment revenue in one year" },
      diagram: "installments",
    },
  },
  {
    id: "cs-checkout",
    type: "case-study",
    media: {
      type: "video",
      src: "/videos/checkout-anim",
      alt: "The checkout walked through before and after the redesign",
    },
    title: "Increasing checkout conversion through systems thinking",
    meta: "Case study · Checkout",
    metrics: [
      { value: "+5–6pp", label: "Blended completion" },
      { value: "+3–5pp", label: "Returning users" },
    ],
    cta: { label: "Read case study", href: "/case-study/checkout" },
    chapter: {
      principle: "See the loops behind the screen.",
      line: "I mapped the loops behind lost payments and turned the four strongest into experiments.",
      outcome: { value: "+5–6pp", label: "blended checkout completion" },
      diagram: "checkout",
    },
  },
];

// Live app / plugin targets, kept in one place so a moved deploy or a renamed
// plugin is a one-line change. An entry left `null` simply renders no action on
// its card, so a project that has nowhere to go yet still lists cleanly.
//
// The apps have no articles: each landing page is the destination on the
// homepage and on /work, so their URLs are exported for the homepage cards.
export const AGAIN_URL = "https://again.spryn.workers.dev/";
export const TRAMAT_URL = "https://tramat.spryn.workers.dev/";

export const CODE_LINKS = {
  again: AGAIN_URL,
  tramat: TRAMAT_URL,
  halftone: "https://www.figma.com/community/plugin/1477582845958874564",
  cssExporter: "https://www.figma.com/community/plugin/1540437242003864494",
  jsonExporter: "https://www.figma.com/community/plugin/1659925678321828532",
};

// Only the targets that exist become actions, so a card never shows a dead link.
const externalLinks = (entries) =>
  entries
    .filter((entry) => entry.href)
    .map((entry) => ({ ...entry, external: true }));

/** @type {WorkItem[]} */
export const codeProjects = [
  {
    id: "code-again",
    type: "code",
    media: {
      type: "image",
      src: imgAgainCard,
      alt: "Again on iPhone: a Chinese test card answered correctly, “to listen” for 听, next to the seven-day streak secured for today",
    },
    title: "Again — Vocabulary app for iOS",
    meta: "App · iOS",
    links: externalLinks([{ label: "View Again", href: CODE_LINKS.again }]),
  },
  {
    id: "code-tramat",
    type: "code",
    media: {
      type: "image",
      src: imgTramatCard,
      alt: "Tramat on iPhone: the Design system project’s board, with six tasks to do and five done, next to that project’s notes",
    },
    title: "Tramat — Task manager for iOS",
    meta: "App · iOS",
    links: externalLinks([{ label: "View Tramat", href: CODE_LINKS.tramat }]),
  },
  {
    id: "code-halftone",
    type: "code",
    media: {
      type: "image",
      src: imgHalftonePlugin,
      alt: "Halftone effect Figma plugin with a dotted preview and dot size, shape and color controls",
    },
    title: "Halftone effect — Figma plugin",
    meta: "Figma plugin",
    links: externalLinks([
      { label: "View Halftone effect", short: "View plugin", href: CODE_LINKS.halftone },
    ]),
  },
  {
    id: "code-design-to-code",
    type: "code",
    media: {
      type: "image",
      src: imgDesignToCodePlugins,
      alt: "Screen JSON Export and Figma CSS Exporter plugin windows side by side",
    },
    title: "Design-to-code toolkit",
    meta: "Figma plugins",
    links: externalLinks([
      { label: "View CSS exporter", short: "CSS exporter", href: CODE_LINKS.cssExporter },
      { label: "View JSON exporter", short: "JSON exporter", href: CODE_LINKS.jsonExporter },
    ]),
  },
];

/** @type {WorkItem[]} */
export const motionProjects = [
  {
    id: "motion-mail",
    type: "visual",
    media: {
      type: "video",
      src: "/videos/mail",
      alt: "An envelope folding and flying off as a message sends",
      // The clip is a light UI on a transparent canvas, so it needs a zinc-100
      // plate rather than the themed gradient behind it.
      background: "var(--zinc-100)",
    },
    title: "Send email interaction",
    meta: "Motion · Rive",
  },
  {
    // The only motion piece that is also playable: the card links to the live
    // demo, so the whole card — including the thumbnail — goes there.
    id: "motion-add-to-bag",
    type: "visual",
    media: {
      type: "video",
      src: "/videos/sneaker",
      alt: "A product card flying into the shopping bag on add to bag",
      background: "var(--zinc-100)",
    },
    title: "Add-to-bag sneaker interaction",
    meta: "Motion · Interactive demo",
    cta: { label: "Try the demo", href: "/add-to-bag-demo" },
  },
];

// UI cards have no page of their own: the card opens the dialog carousel over
// the grid. Each one lists its screens in `gallery`; while a project is still
// down to a single placeholder the dialog drops its arrows and shows just that.
//
// Every UI screen is a framed composition of the same size, so the dialog steps
// through a project without the artwork resizing from one screen to the next.
const uiScreen = (src, alt) => ({ type: "image", src, alt });

// A UI card's thumbnail is one of the project's own screens, picked by name so
// the card and the gallery can never drift apart.
const uiThumbnail = (screens, src) =>
  screens.find((screen) => screen.src === src);

// The order of each gallery is the argument the project makes, not the order the
// screens were drawn in, so it is worth reading as a sentence.

// Collaborative workspace, then branching AI exploration, then the investigation
// itself, the human decision, and the trail the decision leaves behind.
const workspaceScreens = [
  uiScreen(
    imgWorkspaceAlert,
    "The AI Workspace home, with a Risk Assistant alert about a suspicious transaction arriving in the corner"
  ),
  uiScreen(
    imgWorkspaceTeams,
    "A team thread where the Confluence, Figma and Jira assistants settle a checkout change: the Figma Assistant updates seven frames and the Jira Assistant opens the task, beside the checkout file in Figma"
  ),
  uiScreen(
    imgWorkspaceConversationAsk,
    "A sprint health report opened as a conversation map, with side branches on open issues and spillover risk, and one of its recommendations selected to ask AI about"
  ),
  uiScreen(
    imgWorkspaceConversationMap,
    "The same conversation map with a new branch drafted from that recommendation, a question to send to the backend lead"
  ),
  uiScreen(
    imgWorkspaceAnalysis,
    "The flagged transaction opened out: its details, the deposit activity that triggered the rule, and the actions available on it"
  ),
  uiScreen(
    imgWorkspaceMerchantReview,
    "The assistant's analysis of the documents the merchant submitted, ending in a recommendation to clear the user"
  ),
  uiScreen(
    imgWorkspaceClosedTicket,
    "The alert's timeline, from the flag through the information request to the merchant's response and the alert being closed"
  ),
];

// The proposition, then the controls themselves, then what the car reports back,
// then the one manoeuvre run from the phone.
const vehicleScreens = [
  uiScreen(
    imgVehicleOnboarding,
    "Three onboarding screens: control the car from anywhere, park it in tight spaces, and link the vehicle"
  ),
  uiScreen(
    imgVehicleControls,
    "The vehicle home with its estimated range, the full controls panel, and the cabin climate screen"
  ),
  uiScreen(
    imgVehicleStatus,
    "Vehicle status flagging a low rear-left tyre, the car's location on a map, and the garage of linked vehicles"
  ),
  uiScreen(
    imgVehicleRemotePark,
    "Remote parking: choosing a space, holding to move the car into it, and the parked confirmation"
  ),
];

// The proposition, then opening a wallet, then the account the wallets hang off
// and a conversion between them, then a single wallet on its own.
const walletScreens = [
  uiScreen(
    imgWalletsOnboarding,
    "Three onboarding screens: switch between currencies, exchange when the timing works, and pay with the card anywhere"
  ),
  uiScreen(
    imgWalletsAddWallet,
    "Opening a wallet: the account overview, linking the account to the new wallet, and choosing its currency"
  ),
  uiScreen(
    imgWalletsConversion,
    "The account with its currency wallets listed, and a pound-to-dollar conversion with the rate locked and the fee shown"
  ),
  uiScreen(
    imgWalletsDetail,
    "Yuan, pound and dollar wallets, each with its balance, its four actions and its own transaction history"
  ),
];

// The platform, then an answer asked of it, the chat it lives in and a table it
// opens, then the canvas that answer builds, then the operational view, a
// single transaction, and a task carried out.
const portalScreens = [
  uiScreen(
    imgPortalDashboard,
    "The merchant portal dashboard: an assistant prompt over the current, available and in-progress balances and the pay-in and payout charts"
  ),
  uiScreen(
    imgPortalDashboardScroll,
    "Further down the dashboard: pay-ins and payouts by shop over the last seven days, then claims and the average ticket value"
  ),
  uiScreen(
    imgPortalAiAnswer,
    "The assistant answering a question about Mexico in a side panel, offering the canvas it built as a link"
  ),
  uiScreen(
    imgPortalChatExpanded,
    "The same answer with the chat expanded to the full page, beside the history of earlier chats"
  ),
  uiScreen(
    imgPortalShowWidget,
    "Asked for the latest ten transactions of one merchant, the assistant opens them as a table beside the chat"
  ),
  uiScreen(
    imgPortalAiCanvas,
    "The generated Mexico daily overview: pay-ins and payouts split by payment method, and the conversion rate trend"
  ),
  uiScreen(
    imgPortalPayins,
    "The pay-ins table under its performance totals, listing each transaction's status, shop, country and method"
  ),
  uiScreen(
    imgPortalPayinDetail,
    "A single pay-in opened on its payment information, its method, and the fee breakdown down to the net amount credited"
  ),
  uiScreen(
    imgPortalPaymentRequest,
    "A new payment request with its payment, customer and address details confirmed, and delivery set to a payment link that expires after 15 minutes"
  ),
];

/** @type {WorkItem[]} */
export const uiProjects = [
  {
    id: "ui-ai-operations",
    type: "visual",
    media: uiThumbnail(workspaceScreens, imgWorkspaceAlert),
    gallery: workspaceScreens,
    title: "AI workspace for operational teams",
    meta: "Interface · AI operations",
  },
  {
    id: "ui-vehicle-controls",
    type: "visual",
    media: uiThumbnail(vehicleScreens, imgVehicleControls),
    gallery: vehicleScreens,
    title: "Remote vehicle controls",
    meta: "Interface · Automotive",
  },
  {
    id: "ui-cross-border",
    type: "visual",
    media: uiThumbnail(walletScreens, imgWalletsOnboarding),
    gallery: walletScreens,
    title: "Cross-border money movement",
    meta: "Interface · Fintech",
  },
  {
    id: "ui-b2b-saas",
    type: "visual",
    media: uiThumbnail(portalScreens, imgPortalDashboard),
    gallery: portalScreens,
    title: "B2B payments and AI-assisted workflows",
    meta: "Interface · B2B SaaS",
  },
];

// One address, one href. A bare `mailto:` — no subject or body — is what
// Outlook, Apple Mail and Gmail all open as a new message to this inbox.
// Prefilling the body is what made Contact open a blank or the wrong compose
// window on Windows.
export const CONTACT_EMAIL = "orabadanux@gmail.com";
export const CONTACT_MAILTO = `mailto:${CONTACT_EMAIL}`;
// Web3Forms access key. Public by design — it only aliases CONTACT_EMAIL.
export const WEB3FORMS_ACCESS_KEY = "91534a9d-d7d4-4580-bbfe-1cb139014da7";

export const socialLinks = [
  { label: "Mail", href: CONTACT_MAILTO },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/oscar-rabadan/",
    external: true,
  },
  { label: "GitHub", href: "https://github.com/orabadanux", external: true },
  { label: "Figma", href: "https://figma.com/@oscar_rbdn", external: true },
];

export const CV_DOWNLOAD_URL = `${process.env.PUBLIC_URL}/Oscar-Rabadan-Product-Designer-CV.pdf`;
export const CV_DOWNLOAD_FILENAME = "Oscar-Rabadan-Product-Designer-CV.pdf";

// Same-origin files (the CV) should save to disk rather
// than open in the browser's PDF viewer. `download` is honoured for same-origin
// URLs; a temporary anchor is how a DS button can still trigger it.
export function downloadFile(href, filename) {
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename || href.split("/").pop() || "";
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// Single source of truth for the Installments artwork so the homepage card and
// the detail page always share the same images.
export const INSTALLMENTS_IMAGE_TRANSPARENT = imgInstallmentsTransparent;

// The design system's loop runs in two places: its card media above and the
// opening figure of its case study. The other case studies open on a stage of
// their own screens instead. An extension-less /videos path, resolved to a
// webm + mp4 pair by MediaVideo.
export const DESIGN_SYSTEM_ANIMATION = "/videos/ds-hero";
